/**
 * Cart localStorage layout (issue #44), single source of keys for CartContext + server sync.
 */

export const CART_STORAGE_KEY = "dank_client_cart_v2";
export const CART_STORE_PREFIX = "dank_client_cart_store_";

export type PromoCodeState = {
  code: string | null;
  promoCodeId: string | null;
  discountAmount: number;
  /** Best-offer automation (issue #61) vs shopper-entered code */
  source?: "manual" | "auto";
};

export type SerializableCartItem = {
  productId: string;
  name: string;
  catalogItemId: string;
  packageId?: string | null;
  price: number;
  quantity: number;
  actualPackageId?: string | null;
};

export type SavedCartPayloadV1 = {
  v: 1;
  storeCovaLocationId: number | null;
  fulfillmentMode: "pickup" | "delivery";
  perStore: Record<
    string,
    { items: SerializableCartItem[]; promoCode: PromoCodeState | null }
  >;
  temp: { items: SerializableCartItem[]; promoCode: PromoCodeState | null };
};

export function emptySavedCartPayload(): SavedCartPayloadV1 {
  return {
    v: 1,
    storeCovaLocationId: null,
    fulfillmentMode: "pickup",
    perStore: {},
    temp: { items: [], promoCode: null },
  };
}

export function packCartFromLocalStorage(): SavedCartPayloadV1 {
  if (typeof window === "undefined") {
    return emptySavedCartPayload();
  }

  let storeCovaLocationId: number | null = null;
  let fulfillmentMode: "pickup" | "delivery" = "pickup";

  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Record<string, unknown>;
      if (typeof o.storeCovaLocationId === "number" && o.storeCovaLocationId > 0) {
        storeCovaLocationId = o.storeCovaLocationId;
      }
      if (o.fulfillmentMode === "delivery") fulfillmentMode = "delivery";
    }
  } catch {
    /* ignore */
  }

  const perStore: SavedCartPayloadV1["perStore"] = {};
  let temp: SavedCartPayloadV1["temp"] = { items: [], promoCode: null };

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CART_STORE_PREFIX)) continue;
      const suffix = key.slice(CART_STORE_PREFIX.length);
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      let parsed: { items?: SerializableCartItem[]; promoCode?: PromoCodeState | null };
      try {
        parsed = JSON.parse(raw) as typeof parsed;
      } catch {
        continue;
      }
      const items = Array.isArray(parsed.items) ? parsed.items : [];
      const promoCode =
        parsed.promoCode === undefined ? null : parsed.promoCode;

      if (suffix === "_temp") {
        temp = { items, promoCode };
      } else if (/^\d+$/.test(suffix)) {
        perStore[suffix] = { items, promoCode };
      }
    }
  } catch {
    /* ignore */
  }

  return {
    v: 1,
    storeCovaLocationId,
    fulfillmentMode,
    perStore,
    temp,
  };
}

export function applyPackedCartToLocalStorage(p: SavedCartPayloadV1): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify({
        storeCovaLocationId: p.storeCovaLocationId,
        fulfillmentMode: p.fulfillmentMode,
      })
    );

    const toKeep = new Set<string>();

    for (const [storeId, bucket] of Object.entries(p.perStore)) {
      if (!/^\d+$/.test(storeId)) continue;
      const k = `${CART_STORE_PREFIX}${storeId}`;
      toKeep.add(k);
      localStorage.setItem(
        k,
        JSON.stringify({ items: bucket.items, promoCode: bucket.promoCode })
      );
    }

    const tempKey = `${CART_STORE_PREFIX}_temp`;
    toKeep.add(tempKey);
    localStorage.setItem(
      tempKey,
      JSON.stringify({ items: p.temp.items, promoCode: p.temp.promoCode })
    );

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CART_STORE_PREFIX)) continue;
      if (!toKeep.has(key)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}
